import { useState } from 'react';
import type { CardDetailDto, CardPatch, EnergyLevel } from '@konvert/shared';
import { formatMinutesRu, localDateOf, localTimeOf, zonedTimeToInstant } from '@konvert/domain';
import { t } from '../lib/i18n.js';

const TIME_PRESETS = [15, 30, 60, 120, 240];
const DURATION_PRESETS = [30, 60, 90, 120];

const inputClass =
  'w-full min-w-0 rounded-[var(--radius-card)] border border-[var(--line)] bg-paper px-2 py-1.5 text-sm text-ink outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-ink-soft">{label}</span>
      {children}
    </label>
  );
}

/**
 * Editing the fields a card actually has (SPEC 11.2, экран 3). Only changed
 * fields go into the patch, and it is sent with the card version, so a stale
 * screen is refused rather than silently overwriting (SPEC 6).
 */
export function CardEditForm({
  card,
  timezone,
  onCancel,
  onSave,
}: {
  card: CardDetailDto;
  timezone: string;
  onCancel: () => void;
  onSave: (patch: CardPatch) => void;
}): JSX.Element {
  const [title, setTitle] = useState(card.title);
  const [body, setBody] = useState(card.body ?? '');
  const [minutes, setMinutes] = useState<number | null>(card.est_minutes);
  const [energy, setEnergy] = useState<EnergyLevel | null>(card.energy);
  const [deadline, setDeadline] = useState(card.deadline_date ?? '');

  const startDate = card.start_at ? localDateOf(new Date(card.start_at), timezone) : '';
  const startClock = card.start_at ? localTimeOf(new Date(card.start_at), timezone) : '10:00';
  const currentDuration =
    card.start_at && card.end_at
      ? Math.round((+new Date(card.end_at) - +new Date(card.start_at)) / 60_000)
      : 60;

  const [meetingDate, setMeetingDate] = useState(startDate);
  const [meetingStart, setMeetingStart] = useState(startClock);
  const [duration, setDuration] = useState(currentDuration);
  const [location, setLocation] = useState(card.location ?? '');

  const [remindDate, setRemindDate] = useState(card.remind_date ?? '');
  const [remindTime, setRemindTime] = useState(card.remind_time ? card.remind_time.slice(0, 5) : '');

  const save = (): void => {
    const patch: CardPatch = {};
    if (title.trim() && title.trim() !== card.title) patch.title = title.trim();
    if ((body.trim() || null) !== card.body) patch.body = body.trim() || null;

    if (card.kind === 'task') {
      if (minutes !== card.est_minutes) patch.est_minutes = minutes;
      if (energy !== card.energy) patch.energy = energy;
      if ((deadline || null) !== card.deadline_date) patch.deadline_date = deadline || null;
    }

    if (card.kind === 'meeting' && meetingDate) {
      const startAt = zonedTimeToInstant(meetingDate, meetingStart, timezone);
      const endAt = new Date(+startAt + duration * 60_000);
      if (startAt.toISOString() !== card.start_at) patch.start_at = startAt.toISOString();
      if (endAt.toISOString() !== card.end_at) patch.end_at = endAt.toISOString();
      if ((location.trim() || null) !== card.location) patch.location = location.trim() || null;
    }

    if (card.kind === 'reminder') {
      if (remindDate && remindDate !== card.remind_date) patch.remind_date = remindDate;
      const time = remindTime || null;
      if (time !== (card.remind_time ? card.remind_time.slice(0, 5) : null)) patch.remind_time = time;
    }

    onSave(patch);
  };

  return (
    <div className="space-y-3">
      <Field label={t('card.title_field')}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
      </Field>

      {card.kind === 'task' ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {TIME_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setMinutes(preset)}
                className={`rounded-full border px-2.5 py-1 text-xs ${
                  minutes === preset
                    ? 'border-[var(--ink)] text-ink'
                    : 'border-[var(--line)] text-ink-soft'
                }`}
              >
                {formatMinutesRu(preset)}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-3">
            <div className="flex gap-1.5">
              {(['green', 'yellow', 'red'] as EnergyLevel[]).map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setEnergy(level)}
                  aria-label={t(`energy.${level}` as 'energy.green')}
                  className={`h-6 w-6 rounded-[4px] border-2 ${
                    energy === level ? 'border-[var(--ink)]' : 'border-transparent'
                  } ${level === 'green' ? 'bg-green' : level === 'yellow' ? 'bg-amber' : 'bg-brick'}`}
                />
              ))}
            </div>
            <Field label={t('new.deadline')}>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </>
      ) : null}

      {card.kind === 'meeting' ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('new.date')}>
              <input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label={t('new.start')}>
              <input
                type="time"
                value={meetingStart}
                onChange={(e) => setMeetingStart(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {DURATION_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setDuration(preset)}
                className={`rounded-full border px-2.5 py-1 text-xs ${
                  duration === preset
                    ? 'border-[var(--ink)] text-ink'
                    : 'border-[var(--line)] text-ink-soft'
                }`}
              >
                {formatMinutesRu(preset)}
              </button>
            ))}
          </div>
          <Field label={t('new.place')}>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      ) : null}

      {card.kind === 'reminder' ? (
        <div className="grid grid-cols-2 gap-2">
          <Field label={t('new.date')}>
            <input
              type="date"
              value={remindDate}
              onChange={(e) => setRemindDate(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label={t('new.remind_time')}>
            <input
              type="time"
              value={remindTime}
              onChange={(e) => setRemindTime(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      ) : null}

      <Field label={t('card.body_field')}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={card.kind === 'note' ? 5 : 2}
          className={`${inputClass} resize-none`}
        />
      </Field>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-[var(--radius-card)] border border-[var(--line)] px-3 py-2.5 text-ink"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={save}
          className="flex-1 rounded-[var(--radius-card)] bg-[var(--ink)] px-3 py-2.5 text-[var(--paper)]"
        >
          {t('card.save')}
        </button>
      </div>
    </div>
  );
}
